import { graphql } from 'react-relay';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { useFlags } from '@atlaskit/flag';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	useContentStarUnstarMutation as useContentStarUnstarMutation$data,
	useContentStarUnstarMutationVariables as useContentStarUnstarMutation$variables,
} from './__types__/useContentStarUnstarMutation.ts';
import type {
	useContentStarStarMutation as useContentStarStarMutation$data,
	useContentStarStarMutationVariables as useContentStarStarMutation$variables,
} from './__types__/useContentStarStarMutation.ts';
const useContentStarStarMutation = graphql`
	mutation useContentStarStarMutation($contentId: ID!) {
		favouritePage(favouritePageInput: { pageId: $contentId }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
			}
		}
	}
`;
const useContentStarUnstarMutation = graphql`
	mutation useContentStarUnstarMutation($contentId: ID!) {
		unfavouritePage(favouritePageInput: { pageId: $contentId }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
			}
		}
	}
`;
const i18n = defineMessages({
	starMutationErrorTitle: {
		id: 'feed.card.star.mutation.error.title',
		defaultMessage: 'We’re having trouble starring that item',
		description:
			'Title of error flag shown when a user attempts to star the feed card but the action fails.',
	},
	unstarMutationErrorTitle: {
		id: 'feed.card.unstar.mutation.error.title',
		defaultMessage: 'We’re having trouble unstarring that item',
		description:
			'Title of error flag shown when a user attempts to unstar the feed card but the action fails.',
	},
	starMutationErrorDescription: {
		id: 'feed.card.star.mutation.error.description',
		defaultMessage: 'Try it again.',
		description:
			'Body of error flag shown when a user attempts to star/unstar the feed card but the action fails. Shown below the error title',
	},
});

type UseContentStarResponse = [(starred: boolean) => Promise<void>, ApolloErrorInterop | undefined];

export function useContentStar(contentId: string): UseContentStarResponse {
	const { showFlag } = useFlags();
	const onMutationError = useCallback(
		(isStarred: boolean) => () => {
			showFlag({
				description: <FormattedMessage {...i18n.starMutationErrorDescription} />,
				title: (
					<FormattedMessage
						{...(isStarred ? i18n.unstarMutationErrorTitle : i18n.starMutationErrorTitle)}
					/>
				),
				icon: (
					<StatusErrorIcon label="error" color={token('color.icon.danger')} spacing="spacious" />
				),
				isAutoDismiss: true,
			});
		},
		[showFlag],
	);
	const [mutateStar, { error: starError }] = useMutationInterop<
		useContentStarStarMutation$data,
		useContentStarStarMutation$variables
	>(useContentStarStarMutation, {
		variables: { contentId },
		optimisticResponse: {
			favouritePage: {
				__typename: 'FavouritePagePayload',
				content: {
					__typename: 'Content',
					id: contentId,
					metadata: {
						__typename: 'ContentMetadata',
						currentuser: {
							__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
							favourited: {
								// @ts-ignore TS doesn't recognize __typename which is needed for mutation
								__typename: 'FavouritedSummary',
								isFavourite: true,
							},
						},
					},
				},
			},
		},
		onError: onMutationError(false),
	});
	const [mutateUnstar, { error: unstarError }] = useMutationInterop<
		useContentStarUnstarMutation$data,
		useContentStarUnstarMutation$variables
	>(useContentStarUnstarMutation, {
		variables: { contentId },
		optimisticResponse: {
			unfavouritePage: {
				__typename: 'FavouritePagePayload',
				content: {
					__typename: 'Content',
					id: contentId,
					metadata: {
						__typename: 'ContentMetadata',
						currentuser: {
							// @ts-ignore TS doesn't recognize __typename which is needed for mutation
							__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
							favourited: null,
						},
					},
				},
			},
		},
		onError: onMutationError(true),
	});
	const toggleStarState = useCallback(
		async (starred: boolean) => {
			try {
				if (starred) {
					await mutateUnstar();
				} else {
					await mutateStar();
				}
			} catch {} // errors handled via useMutation
		},
		[mutateStar, mutateUnstar],
	);

	return [toggleStarState, starError || unstarError];
}
